import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { useState } from "react";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Loading from "../../../../components/loading/loading";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  importClientFormSectionsRequest,
  importFormTemplateSectionsRequest,
} from "../../../../utils/APICalls";
import { getClientFormSectionsRequest } from "../../../../redux/clients/slice";
import { getFormSectionsRequest } from "../../../../redux/form-templates/slice";

const styles = {
  headerTxt: {
    fontWeight: "540",
    letterSpacing: "0.6px",
    fontSize: 20,
  },
  closeBtn: {
    position: "absolute",
    right: 12,
    top: 12,
    color: "rgba(0, 0, 0, 0.54)",
  },
  layout: {
    width: "auto",
    mt: -2,
  },
  message: {
    color: "rgba(0, 0, 0, 0.87)",
    mb: 0.5,
  },
  actionBtn: {
    ml: 2,
    mr: 1,
  },
  buttonLayout: {
    my: 0.5,
  },
  buttonTxt: {
    fontWeight: "510",
    letterSpacing: "0.5px",
    fontSize: 14,
  },
};

export default function ImportTemplateDialog({
  show,
  close,
  templateId,
  isClientForm,
}: any) {
  const [selectedFile, setSelectedFile] = useState<any>();
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setError(false);
    const file = event.target.files?.[0];
    setSelectedFile(file);
    if (!file) return;
    const allowedTypes = ["application/json"];
    const allowedExtensions = [".json"];
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(extension)
    ) {
      setError(true);
      setErrorMessage("Unsupported file type. Only .json file is allowed.");
      return;
    }
    if (!isFileSizeValid(file)) {
      return;
    }
  };

  const handleDelete = () => {
    setSelectedFile(null);
    setError(false);
    setErrorMessage("");
  };

  const isFileSizeValid = (file: any) => {
    if (file?.size > 3000000) {
      setError(true);
      setErrorMessage("File size exceeds 3MB limit.");
      return false;
    }
    return true;
  };

  const onImportClick = async () => {
    if (!selectedFile) {
      setError(true);
      setErrorMessage("Please select a file to save.");
      return;
    }

    if (!isFileSizeValid(selectedFile)) {
      return;
    }

    setLoading(true);
    if (isClientForm) {
      const response = await importClientFormSectionsRequest(
        templateId,
        selectedFile
      );
      setLoading(false);
      if (response?.status === 204) {
        dispatch(getClientFormSectionsRequest({ id: templateId }));
        close();
      } else if (response?.status === 400) {
        setError(true);
        setErrorMessage(response.data?.detail);
      }
    } else {
      const response = await importFormTemplateSectionsRequest(
        templateId,
        selectedFile
      );
      setLoading(false);
      if (response?.status === 204) {
        dispatch(getFormSectionsRequest({ id: templateId }));
        close();
      } else if (response?.status === 400) {
        setError(true);
        setErrorMessage(response.data?.detail);
      }
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={show}
      onClose={close}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={styles.headerTxt}>
        Import Form Elements
      </DialogTitle>
      <Divider />
      <IconButton aria-label="close" onClick={close} sx={styles.closeBtn}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.layout}>
        <Typography sx={{ mt: 1 }}>
          Import form elements that have been exported from another template
          within Vision.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            backgroundColor: "#FFF4E5",
            px: 2,
            py: 1.5,
            mt: 2,
          }}
        >
          <WarningAmberOutlinedIcon color="primary" />
          <Typography fontSize={14} color="#663C00" ml={1}>
            Importing will replace the current form elements.
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 2,
            height: 152,
            width: "100%",
            border: "1px dashed rgba(0, 0, 0, 0.12)",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <UploadFileIcon color="primary" />
          <Box sx={{ display: "flex", mt: 1 }}>
            <Typography color="primary" sx={{ textDecoration: "underline" }}>
              Click to upload
            </Typography>
            <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
          </Box>
          <Typography sx={{ color: "rgba(0, 0, 0, 0.6)", mt: 1 }}>
            .json (max. 3MB)
          </Typography>
          <input
            style={{
              display: "block",
              height: 152,
              width: "100%",
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              opacity: 0,
              cursor: "pointer",
            }}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            name="upload"
            onClick={(event) => {
              // same file selection issue
              (event.target as HTMLInputElement).value = "";
            }}
          />
        </Box>
        {selectedFile && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1.5px solid green",
              p: 0.8,
              borderRadius: 1,
              backgroundColor: "#F7FAF7",
            }}
          >
            <Box display={"flex"} alignItems={"center"}>
              <AttachFileOutlinedIcon color="primary" />
              <Box sx={{ ml: 1.5 }}>
                <Typography>
                  {selectedFile?.name || "No file selected"}
                </Typography>
                <Typography>
                  {" "}
                  {selectedFile
                    ? `${(selectedFile.size / 1024).toFixed(2)} kb`
                    : ""}
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton aria-label="delete" onClick={handleDelete}>
                <DeleteOutlinedIcon color="primary" />
              </IconButton>
            </Box>
          </Box>
        )}
        {error && (
          <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={styles.buttonLayout}>
        <Button variant="outlined" onClick={close}>
          <Typography sx={styles.buttonTxt}>Cancel</Typography>
        </Button>
        <Button
          sx={styles.actionBtn}
          variant="contained"
          disabled={!selectedFile || error}
          onClick={onImportClick}
          autoFocus
        >
          <Typography sx={styles.buttonTxt}>Import</Typography>
        </Button>
      </DialogActions>
      {loading && <Loading />}
    </Dialog>
  );
}
